import { Link } from "react-router-dom";
import type { TeamMember } from "@/data/teamMembers";

const InstagramIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" />
    <circle cx="12" cy="12" r="3.75" />
    <circle cx="17.5" cy="6.5" r="0.75" fill="currentColor" stroke="none" />
  </svg>
);

const FacebookIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M13.5 21v-7h2.35l.4-3h-2.75V9.08c0-.87.24-1.46 1.5-1.46H16.4V4.94c-.24-.03-1.06-.1-2.02-.1-2 0-3.38 1.22-3.38 3.47V11H8.75v3H11v7h2.5Z" />
  </svg>
);

const getSocialIcon = (label: string) => {
  const normalized = label.toLowerCase();
  if (normalized.includes("facebook")) return FacebookIcon;
  return InstagramIcon;
};

interface TeamMemberCardProps {
  member: TeamMember;
}

const TeamMemberCard = ({ member }: TeamMemberCardProps) => (
  <Link
    to={member.profileUrl}
    className="group block h-full overflow-hidden border border-white/8 bg-[#0f1a15] shadow-[0_30px_70px_-40px_rgba(0,0,0,0.42)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_32px_72px_-18px_rgba(0,0,0,0.48)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
  >
    <div className="h-[2px] bg-accent w-full" />

    <div className="relative aspect-[3/4] overflow-hidden">
      <img
        src={member.image}
        alt={member.imageAlt}
        className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        loading="lazy"
        decoding="async"
        width={900}
        height={1200}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

      <div className="absolute bottom-0 left-0 right-0 p-6">
        <h3 className="font-headline text-2xl text-white leading-tight">{member.name}</h3>
        <p className="font-body text-[10px] uppercase tracking-[0.22em] text-accent mt-1.5">{member.title}</p>
      </div>
    </div>

    <div className="flex items-start justify-between gap-4 px-6 py-5 md:px-7 md:py-6">
      <div className="space-y-1.5 min-w-0">
        <a
          href={`mailto:${member.email}`}
          onClick={(e) => e.stopPropagation()}
          className="block truncate font-body text-xs text-white/60 hover:text-accent transition-colors"
        >
          {member.email}
        </a>
        <a
          href={`tel:${member.phone.replace(/\D/g, "")}`}
          onClick={(e) => e.stopPropagation()}
          className="block font-body text-xs text-white/40 hover:text-accent transition-colors"
        >
          {member.phone}
        </a>
        {member.socialLinks.length ? (
          <div className="flex flex-wrap gap-2 pt-2">
            {member.socialLinks.map((social) => {
              const Icon = getSocialIcon(social.label);
              return (
                <a
                  key={`${member.id}-${social.label}`}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`${member.name} on ${social.label}`}
                  title={social.label}
                  className="inline-flex h-11 w-11 items-center justify-center border border-white/15 text-white/60 transition-colors hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                >
                  <Icon />
                </a>
              );
            })}
          </div>
        ) : null}
      </div>
      <span className="font-body text-[10px] uppercase tracking-[0.18em] text-accent/60 group-hover:text-accent transition-colors whitespace-nowrap shrink-0">
        View Profile →
      </span>
    </div>
  </Link>
);

export default TeamMemberCard;
